/**
 * DecreeTemplateFormModal — Ustawienia → Szablony dekretacji (create/edit pojedynczego
 * szablonu). Mirror strukturalny `ChartOfAccountFormModal` (firma NIE jest polem — kontekst
 * ustala rodzic `DecreeTemplatesTab`; `documentClass` IMMUTABLE po utworzeniu — patrz
 * nagłówek `apps/api/app/api/decree-templates/[id]/route.ts`, więc pole jest disabled
 * w trybie edycji).
 *
 * Siatka wierszy wzorca (`lines`) — flat table (BEZ grupowania wizualnego jak
 * `DecreeLinesEditor`, bo tu edytujemy WZORZEC, nie konkretny dekret z bilansem do
 * pilnowania na żywo): grupa (numer), strona Wn/Ma, konto (`AccountSearchPicker`, wymaga
 * `companyId` z rodzica), źródło kwoty (net/vat/gross), opis, usuń.
 *
 * `documentTypeId` — opcjonalny, lista z `useDocumentTypes(documentClass, {companyId})`
 * (ten sam hook co selektor typu przy tworzeniu faktury) — zawężona do klasy i firmy
 * wybranej w rodzicu.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { AccountSearchPicker } from '@/components/documents/AccountSearchPicker';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import {
  type DecreeTemplateAmountSource,
  type DecreeTemplateLineDraft,
  type DecreeTemplateSide,
  emptyTemplateLine,
  nextTemplateGroupNo,
  validateTemplateLines,
} from '@/lib/documents/decree-templates';
import { useDocumentTypes } from '@/lib/use-document-types';

export type DecreeTemplateDocumentClass = 'purchase_invoice' | 'sale_invoice';

export interface DecreeTemplateFormValues {
  name: string;
  documentClass: DecreeTemplateDocumentClass;
  /** '' = brak typu (szablon dotyczy całej klasy dokumentu, nie jednego typu). */
  documentTypeId: string;
  isActive: boolean;
  /** true = szablon „dla wszystkich firm" (companyId: null). Immutable po utworzeniu. */
  forAllCompanies: boolean;
  lines: DecreeTemplateLineDraft[];
}

export function emptyDecreeTemplateFormValues(
  documentClass: DecreeTemplateDocumentClass = 'purchase_invoice',
): DecreeTemplateFormValues {
  return {
    name: '',
    documentClass,
    documentTypeId: '',
    isActive: true,
    forAllCompanies: false,
    lines: [],
  };
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: DecreeTemplateFormValues;
  /** Firma właściciela szablonu — kontekst ustala rodzic (`DecreeTemplatesTab`). */
  companyId: string | null;
  /**
   * Czy user może tworzyć szablon „dla wszystkich firm". Backend dopuszcza wspólne szablony
   * tylko super-userowi (`can(ctx, MANAGE, {})` odrzuca nie-super-userów) — mirror procedur.
   * Gdy false, checkbox „Wszystkie firmy" nie pokazuje się przy tworzeniu (koniec ślepego 403).
   */
  allowAllCompanies?: boolean;
  saving?: boolean;
  onSubmit: (values: DecreeTemplateFormValues) => void;
  onClose: () => void;
}

const SIDE_OPTIONS: DecreeTemplateSide[] = ['debit', 'credit'];
const AMOUNT_SOURCE_OPTIONS: DecreeTemplateAmountSource[] = ['net', 'vat', 'gross'];

export function DecreeTemplateFormModal({
  open,
  mode,
  initialValues,
  companyId,
  allowAllCompanies = false,
  saving = false,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [invalidLineKeys, setInvalidLineKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setErrors({});
      setInvalidLineKeys(new Set());
    }
  }, [open, initialValues]);

  // Szablon „dla wszystkich firm" nie ma firmy → konta wpisuje się ręcznie (numer rozwiąże się
  // w planie kont każdej firmy przy użyciu), a typy dokumentów nie są zawężane do firmy.
  const effectiveCompanyId = values.forAllCompanies ? null : companyId;
  const documentTypesQ = useDocumentTypes(values.documentClass, { companyId: effectiveCompanyId });

  function set<K extends keyof DecreeTemplateFormValues>(
    key: K,
    value: DecreeTemplateFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function patchLine(key: string, patch: Partial<DecreeTemplateLineDraft>) {
    setValues((prev) => ({
      ...prev,
      lines: prev.lines.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    }));
  }

  function addLine() {
    setValues((prev) => ({
      ...prev,
      lines: [...prev.lines, emptyTemplateLine(nextTemplateGroupNo(prev.lines))],
    }));
  }

  function removeLine(key: string) {
    setValues((prev) => ({ ...prev, lines: prev.lines.filter((line) => line.key !== key) }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.name.trim()) {
      next.name = t('tenant.documents.decree.templates.form.errors.nameRequired');
    } else if (values.name.trim().length > 200) {
      next.name = t('tenant.documents.decree.templates.form.errors.nameLength');
    }
    setErrors(next);

    const invalidLines = validateTemplateLines(values.lines);
    setInvalidLineKeys(invalidLines);

    return Object.keys(next).length === 0 && invalidLines.size === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({ ...values, name: values.name.trim() });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        mode === 'create'
          ? 'tenant.documents.decree.templates.form.titleCreate'
          : 'tenant.documents.decree.templates.form.titleEdit',
      )}
      size="xl"
      guardUnsaved
      // Wiersze dekretu, strona i źródło kwoty powstają kliknięciem (bez zdarzeń wpisywania) —
      // „zmieniono" liczymy porównaniem wartości ze stanem startowym.
      dirty={JSON.stringify(values) !== JSON.stringify(initialValues)}
      // W trakcie zapisu nie da się zamknąć okna tłem ani Esc (jak „Anuluj").
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="decree-template-form-submit"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="decree-template-form">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            label={t('tenant.documents.decree.templates.form.name')}
            required
            error={errors.name}
          >
            <Input
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              maxLength={200}
              data-testid="decree-template-form-name"
            />
          </FormField>

          <FormField label={t('tenant.documents.decree.templates.form.documentClass')} required>
            <Dropdown
              value={values.documentClass}
              onChange={(v) => {
                set('documentClass', v as DecreeTemplateDocumentClass);
                set('documentTypeId', '');
              }}
              disabled={mode === 'edit'}
              data-testid="decree-template-form-class"
              options={[
                {
                  value: 'purchase_invoice',
                  label: t('tenant.documents.decree.templates.documentClasses.purchase_invoice'),
                },
                {
                  value: 'sale_invoice',
                  label: t('tenant.documents.decree.templates.documentClasses.sale_invoice'),
                },
              ]}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('tenant.documents.decree.templates.form.documentType')}>
            <Dropdown
              value={values.documentTypeId}
              onChange={(v) => set('documentTypeId', v)}
              disabled={documentTypesQ.isLoading}
              data-testid="decree-template-form-type"
              options={[
                { value: '', label: t('tenant.documents.decree.templates.form.documentTypeNone') },
                ...documentTypesQ.data.map((dt) => ({ value: dt.id, label: dt.name })),
              ]}
            />
          </FormField>

          <div className="flex flex-col justify-end gap-2 pb-2">
            {/* Checkbox „Wszystkie firmy": przy tworzeniu tylko dla super-usera (backend i tak
                odrzuci pozostałych); przy edycji pokazujemy stan wspólnego szablonu (disabled). */}
            {((mode === 'create' && allowAllCompanies) ||
              (mode === 'edit' && values.forAllCompanies)) && (
              <div>
                <Checkbox
                  label={t('tenant.documents.decree.templates.form.forAllCompanies')}
                  checked={values.forAllCompanies}
                  onChange={(e) => set('forAllCompanies', e.target.checked)}
                  disabled={mode === 'edit'}
                  data-testid="decree-template-form-all-companies"
                />
                {values.forAllCompanies && (
                  <p className="mt-1 pl-6 text-xs text-text-muted">
                    {t('tenant.documents.decree.templates.form.forAllCompaniesHint')}
                  </p>
                )}
              </div>
            )}
            <Checkbox
              label={t('tenant.documents.decree.templates.form.isActive')}
              checked={values.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              data-testid="decree-template-form-is-active"
            />
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-text-primary">
              {t('tenant.documents.decree.templates.form.linesTitle')}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={addLine}
              data-testid="decree-template-form-add-line"
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t('tenant.documents.decree.templates.form.addLine')}
            </Button>
          </div>

          {values.lines.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center text-sm text-text-tertiary">
              {t('tenant.documents.decree.templates.form.emptyLines')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border-primary">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border-primary bg-bg-secondary">
                    <th className="px-2 py-2 text-left text-xs font-semibold text-text-secondary">
                      {t('tenant.documents.decree.templates.form.colGroup')}
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-text-secondary">
                      {t('tenant.documents.decree.templates.form.colSide')}
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-text-secondary">
                      {t('tenant.documents.decree.templates.form.colAccount')}
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-text-secondary">
                      {t('tenant.documents.decree.templates.form.colAmountSource')}
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-text-secondary">
                      {t('tenant.documents.decree.templates.form.colDescription')}
                    </th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {values.lines.map((line) => (
                    <tr
                      key={line.key}
                      className={[
                        'border-b border-border-primary last:border-0',
                        invalidLineKeys.has(line.key) ? 'bg-red-50 dark:bg-red-900/10' : '',
                      ].join(' ')}
                      data-testid={`decree-template-line-row-${line.key}`}
                    >
                      <td className="px-2 py-1 align-top">
                        <input
                          type="number"
                          min={1}
                          className="w-16 rounded border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-primary"
                          value={line.groupNo}
                          onChange={(e) =>
                            patchLine(line.key, { groupNo: Number(e.target.value) || 1 })
                          }
                          aria-label={t('tenant.documents.decree.templates.form.colGroup')}
                        />
                      </td>
                      <td className="px-2 py-1 align-top">
                        <Dropdown
                          value={line.side}
                          onChange={(v) => patchLine(line.key, { side: v as DecreeTemplateSide })}
                          aria-label={t('tenant.documents.decree.templates.form.colSide')}
                          options={SIDE_OPTIONS.map((side) => ({
                            value: side,
                            label: t(`tenant.documents.decree.columns.${side}`),
                          }))}
                        />
                      </td>
                      <td className="px-2 py-1 align-top">
                        {effectiveCompanyId ? (
                          <AccountSearchPicker
                            value={line.accountId || null}
                            selectedLabel={line.accountNumber || null}
                            companyId={effectiveCompanyId}
                            onSelect={(account) =>
                              patchLine(line.key, {
                                accountId: account?.id ?? '',
                                accountNumber: account?.accountNumber ?? '',
                              })
                            }
                            data-testid={`decree-template-line-account-${line.key}`}
                          />
                        ) : (
                          // Szablon „dla wszystkich firm" — brak jednej firmy, więc numer konta
                          // wpisujemy ręcznie (rozwiąże się w planie kont każdej firmy przy użyciu).
                          <Input
                            value={line.accountNumber}
                            onChange={(e) =>
                              patchLine(line.key, { accountId: '', accountNumber: e.target.value })
                            }
                            maxLength={30}
                            placeholder={t(
                              'tenant.documents.decree.templates.form.accountNumberManual',
                            )}
                            aria-label={t('tenant.documents.decree.templates.form.colAccount')}
                            data-testid={`decree-template-line-account-${line.key}`}
                          />
                        )}
                      </td>
                      <td className="px-2 py-1 align-top">
                        <Dropdown
                          value={line.amountSource}
                          onChange={(v) =>
                            patchLine(line.key, {
                              amountSource: v as DecreeTemplateAmountSource,
                            })
                          }
                          aria-label={t('tenant.documents.decree.templates.form.colAmountSource')}
                          options={AMOUNT_SOURCE_OPTIONS.map((source) => ({
                            value: source,
                            label: t(`tenant.documents.decree.templates.amountSources.${source}`),
                          }))}
                        />
                      </td>
                      <td className="px-2 py-1 align-top">
                        <Input
                          value={line.description}
                          onChange={(e) => patchLine(line.key, { description: e.target.value })}
                          maxLength={1000}
                          aria-label={t('tenant.documents.decree.templates.form.colDescription')}
                        />
                      </td>
                      <td className="px-2 py-1 text-center align-top">
                        <button
                          type="button"
                          onClick={() => removeLine(line.key)}
                          className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-red-600"
                          aria-label={t('common.delete')}
                          data-testid={`decree-template-line-remove-${line.key}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {invalidLineKeys.size > 0 && (
            <p className="mt-2 text-xs text-red-600">
              {t('tenant.documents.decree.templates.form.linesIncomplete')}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
